/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import LegalbirdIoModal from "../../Modal/LegalbirdIoModal";
import { Typography } from "@mui/material";
import ButtonLoading from "../../Button/ButtonLoading";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { ProductUnion } from "../../../types/ProductUnion";
import { apiGet, apiPost } from "../../../services/Api/apiCall";
import { InsurerPartner } from "../../../types/InsurerPartner";
import SelectElement from "../../ReactHookFormElements/SelectElement";
import MenuItem from "@mui/material/MenuItem";
import { FormContainer, useForm } from "react-hook-form-mui";
import { translate } from "../../../services/Translations/translatorService";
import { requiredFieldDefaultFieldBased } from "../../../services/reactHookFormValidationRules";
import { useNavigate } from "react-router-dom";
import { getCaseLink } from "../../../services/Product/ProductService/productService";
import { useState } from "react";
import { useSnackbar } from "notistack";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";

type ProductChangeModalProps = {
  product: ProductUnion;
  open: boolean;
  handleClose: Function;
};

type ProductChangeFormData = {
  productType: ProductUnion["productClassName"] | null;
};

export default function ProductChangeModal({ product, open, handleClose }: ProductChangeModalProps) {
  const [isFetching, setIsFetching] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();

  const { data: productInsurerPartner } = useQuery({
    queryKey: queryKeys.all("/lb/services/get_product_insurer_partner/" + product.id),
    queryFn: () => apiGet<InsurerPartner>("/lb/services/get_product_insurer_partner", product.id),
    enabled: !!product,
  });

  async function onSubmit(formData: ProductChangeFormData) {
    if (!formData.productType) {
      return;
    }

    setIsFetching(true);

    try {
      await apiPost("/lb/services/pending_case/convert_to_final_case", {
        productType: formData.productType,
        productId: product.id,
        backofficeUserId: currentUser.id,
      });

      setIsFetching(false);
      handleClose();

      const caseLink = getCaseLink({
        productClassName: formData.productType,
        id: product.id,
      });

      navigate(caseLink);
      enqueueSnackbar("Produkt wurde gewechselt.", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } catch (e) {
      setIsFetching(false);
    }
  }

  const formContext = useForm<ProductChangeFormData>({
    mode: "onBlur",
    defaultValues: {
      productType: null,
    },
  });

  return (
    <LegalbirdIoModal
      title={"Produkt wechseln"}
      open={open}
      handleClose={handleClose}
      submitButton={
        <ButtonLoading
          disabled={!formContext.formState.isValid}
          variant={"contained"}
          onClick={formContext.handleSubmit(onSubmit)}
          isLoading={isFetching}
        >
          Produkt wechseln
        </ButtonLoading>
      }
    >
      <FormContainer formContext={formContext}>
        <Typography>
          Bitte im untenstehenden Modal das Produkt für den Fall auswählen. Dies sollte nur in Absprache mit der
          Mandantschaft erfolgen.
        </Typography>
        <SelectElement name={"productType"} label={"Produkt"} validation={requiredFieldDefaultFieldBased}>
          {productInsurerPartner?.flatFeeCoveredProducts.map((productClassName: string) => {
            return (
              <MenuItem key={productClassName} value={productClassName}>
                {translate(`${productClassName}.label`)}
              </MenuItem>
            );
          })}
        </SelectElement>
      </FormContainer>
    </LegalbirdIoModal>
  );
}
